import EditableShape from '@recogito/annotorious/src/tools/EditableShape';
import { SVG_NAMESPACE } from '@recogito/annotorious/src/util/SVG';
import { setFormatterElSize } from '@recogito/annotorious/src/util/Formatting';
import { getAnnotationStyle } from 'components/ImageViewer/annotorious/formatter';
import {
  createPathElement,
  getPathData,
  getPointsBBox,
  toFreehandTarget,
} from 'components/ImageViewer/annotorious/tools/freehand/freehandUtils';

/**
 * An editable pencil stroke. Points are stored on the annotation's selector
 * (see toFreehandTarget); this class renders the stroke while it is selected
 * and supports dragging to move and corner handles to scale the stroke.
 */
export default class EditableFreehand extends EditableShape {
  constructor(annotation, g, config, env) {
    super(annotation, g, config, env);

    this.svg.addEventListener('mousemove', this.onMouseMove);
    this.svg.addEventListener('mouseup', this.onMouseUp);

    const selector = annotation.selector('FragmentSelector');
    this.originalPoints = (selector?.points || []).map(([x, y]) => [x, y]);
    this.points = this.originalPoints.map(([x, y]) => [x, y]);

    const { color, strokeWidth } = getAnnotationStyle(annotation);

    this.containerGroup = document.createElementNS(SVG_NAMESPACE, 'g');

    this.elementGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    this.elementGroup.setAttribute('class', 'a9s-annotation editable selected');
    this.elementGroup.setAttribute('data-id', annotation.id);

    // Wide invisible stroke makes the thin pencil stroke easy to grab.
    this.hitPath = createPathElement('a9s-freehand-hit');
    this.hitPath.setAttribute('stroke', 'transparent');
    this.hitPath.setAttribute('stroke-width', 16);
    this.hitPath.style.pointerEvents = 'stroke';
    this.hitPath.addEventListener('mousedown', this.onGrab('move'));

    this.innerPath = createPathElement('a9s-inner');
    this.innerPath.style.pointerEvents = 'none';
    this.innerPath.style.stroke = color;
    this.innerPath.style.strokeWidth = strokeWidth;
    this.innerPath.style.fill = 'none';

    this.elementGroup.appendChild(this.hitPath);
    this.elementGroup.appendChild(this.innerPath);

    this.handles = [0, 1, 2, 3].map((index) => {
      const handle = this.drawHandle(0, 0);
      handle.addEventListener('mousedown', this.onGrab('scale', index));
      this.elementGroup.appendChild(handle);

      return handle;
    });

    this.containerGroup.appendChild(this.elementGroup);
    g.appendChild(this.containerGroup);

    this.render();

    this.drag = null;
  }

  onScaleChanged = () => this.handles.map(this.scaleHandle);

  setPoints(points) {
    this.points = points;
    this.render();
  }

  render() {
    const pathData = getPathData(this.points);
    this.hitPath.setAttribute('d', pathData);
    this.innerPath.setAttribute('d', pathData);

    const bbox = getPointsBBox(this.points);
    const corners = [
      [bbox.x, bbox.y],
      [bbox.x + bbox.w, bbox.y],
      [bbox.x + bbox.w, bbox.y + bbox.h],
      [bbox.x, bbox.y + bbox.h],
    ];

    corners.forEach(([x, y], index) =>
      this.setHandleXY(this.handles[index], x, y),
    );

    setFormatterElSize(this.elementGroup, bbox.x, bbox.y, bbox.w, bbox.h);
  }

  onGrab = (mode, handleIndex) => (evt) => {
    if (evt.button !== 0) return;

    evt.preventDefault();

    this.drag = {
      mode,
      handleIndex,
      start: this.getSVGPoint(evt),
      points: this.points.map(([x, y]) => [x, y]),
      bbox: getPointsBBox(this.points),
    };
  };

  onMouseMove = (evt) => {
    if (evt.button !== 0 || !this.drag) return;

    const pos = this.getSVGPoint(evt);
    const { mode, start, points, bbox, handleIndex } = this.drag;

    if (mode === 'move') {
      const deltaX = pos.x - start.x;
      const deltaY = pos.y - start.y;

      this.updatedPoints = points.map(([x, y]) => [x + deltaX, y + deltaY]);
    } else {
      const anchorIndex = (handleIndex + 2) % 4;
      const anchors = [
        [bbox.x, bbox.y],
        [bbox.x + bbox.w, bbox.y],
        [bbox.x + bbox.w, bbox.y + bbox.h],
        [bbox.x, bbox.y + bbox.h],
      ];
      const [anchorX, anchorY] = anchors[anchorIndex];

      const width = pos.x - anchorX;
      const height = pos.y - anchorY;
      const x = width > 0 ? anchorX : pos.x;
      const y = height > 0 ? anchorY : pos.y;
      const w = Math.max(1, Math.abs(width));
      const h = Math.max(1, Math.abs(height));

      this.updatedPoints = points.map(([px, py]) => [
        x + (px - bbox.x) * (w / bbox.w),
        y + (py - bbox.y) * (h / bbox.h),
      ]);
    }

    this.setPoints(this.updatedPoints);
  };

  onMouseUp = () => {
    if (!this.drag) return;

    this.drag = null;
    this.originalPoints = this.points.map(([x, y]) => [x, y]);

    this.emit('update', toFreehandTarget(this.points, this.env.image));
  };

  get element() {
    return this.elementGroup;
  }

  updateState = (annotation) => {
    const selector = annotation.selector('FragmentSelector');
    const points = (selector?.points || []).map(([x, y]) => [x, y]);

    this.originalPoints = points;
    this.setPoints(points);
  };

  destroy() {
    this.containerGroup.parentNode.removeChild(this.containerGroup);
    super.destroy();
  }
}
